import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Chip,
  createRestaurantAvatarProps,
  Icons,
  PushView,
  Skeleton,
  Spinner,
  Typography,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { FlashList } from '@shopify/flash-list';
import { Pressable } from 'react-native';
import { RestaurantListData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/sales-portal-restaurant-transformer';
import { SearchInput } from '@/experiences/sales-portal/components/search-input';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const ROW_ESTIMATED_HEIGHT = 64;
const SKELETON_ROW_COUNT = 8;
const SKELETON_ROWS = Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => i);

const keyExtractor = (restaurant: RestaurantListData) => restaurant.key;

interface OrderEntryMobileRestaurantsPushViewProps {
  hasError: boolean;
  isFetchingMore: boolean;
  isLoading: boolean;
  isOpen: boolean;
  onClearSearch: () => void;
  onClose: () => void;
  onEndReached: () => void;
  onRetry: () => void;
  onSearch: (term: string) => void;
  onSelect: (restaurant: RestaurantListData) => void;
  restaurants: RestaurantListData[];
  selectedRestaurantUUID: string;
}

export const OrderEntryMobileRestaurantsPushView: React.FC<
  OrderEntryMobileRestaurantsPushViewProps
> = ({
  hasError,
  isFetchingMore,
  isLoading,
  isOpen,
  onClearSearch,
  onClose,
  onEndReached,
  onRetry,
  onSearch,
  onSelect,
  restaurants,
  selectedRestaurantUUID,
}) => {
  const [styles, theme] = useStyles();
  const insets = useSafeAreaInsets();
  const [searchText, setSearchText] = useState('');

  const listContentStyle = useMemo(
    () => ({
      ...styles.listContent,
      paddingBottom: theme.sk.spacing.spaceMedium + insets.bottom,
    }),
    [insets.bottom, styles.listContent, theme.sk.spacing.spaceMedium],
  );

  useEffect(() => {
    if (!isOpen) {
      setSearchText('');
    }
  }, [isOpen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose],
  );

  const handleSearchChange = useCallback(
    (term: string) => {
      setSearchText(term);

      if (term.trim()) {
        onSearch(term);
        return;
      }
      onClearSearch();
    },
    [onClearSearch, onSearch],
  );

  const renderItem = useCallback(
    ({ item }: { item: RestaurantListData }) => (
      <Pressable
        accessibilityLabel={
          item.onHold
            ? `${item.restaurantName} — ${i18n.t('sales_portal.restaurant_selector.on_hold')}`
            : item.restaurantName
        }
        accessibilityRole="button"
        accessibilityState={{ selected: item.key === selectedRestaurantUUID }}
        onPress={() => onSelect(item)}
      >
        <XStack
          alignItems="center"
          backgroundColor={
            item.key === selectedRestaurantUUID
              ? '$bgSecondaryDefault'
              : '$transparent'
          }
          borderRadius="$12"
          gap="$12"
          padding="$12"
        >
          <Avatar
            {...createRestaurantAvatarProps(item.restaurantName, item.key)}
            size="$md"
          />
          <YStack flex={1} gap="$2" minWidth={0}>
            <Typography numberOfLines={1} textAlign="left" variant="label">
              {item.restaurantName}
            </Typography>
            <Typography
              color="$textSecondary"
              numberOfLines={1}
              size="$sm"
              textAlign="left"
            >
              {item.restaurantAddress}
            </Typography>
          </YStack>
          {item.onHold ? (
            <Chip
              backgroundColor="$bgError"
              borderColor="$red4"
              circular
              size="$sm"
            >
              <Chip.Icon color="$iconNegativePrimary">
                <Icons.AlertCircle />
              </Chip.Icon>
              <Chip.Text color="$textError">
                {i18n.t('sales_portal.restaurant_selector.on_hold')}
              </Chip.Text>
            </Chip>
          ) : null}
        </XStack>
      </Pressable>
    ),
    [onSelect, selectedRestaurantUUID],
  );

  let list: React.ReactNode;
  if (hasError) {
    list = (
      <ErrorState
        description={i18n.t(
          'sales_portal.restaurant_selector.error.description',
        )}
        onPress={onRetry}
        title={i18n.t('sales_portal.order_entry.error.table.title')}
      />
    );
  } else if (isLoading && restaurants.length === 0) {
    list = (
      <YStack gap="$12" padding="$12">
        {SKELETON_ROWS.map((row) => (
          <XStack alignItems="center" gap="$12" key={row}>
            <Skeleton height="$40" variant="circle" width="$40" />
            <YStack flex={1} gap="$4">
              <Skeleton height="$16" width="60%" />
              <Skeleton height="$14" width="40%" />
            </YStack>
          </XStack>
        ))}
      </YStack>
    );
  } else {
    list = (
      <FlashList
        automaticallyAdjustKeyboardInsets
        contentContainerStyle={listContentStyle}
        data={restaurants}
        estimatedItemSize={ROW_ESTIMATED_HEIGHT}
        keyboardShouldPersistTaps="handled"
        keyExtractor={keyExtractor}
        ListEmptyComponent={
          <YStack alignItems="center" paddingHorizontal="$16" paddingTop="$40">
            <Typography color="$textTertiary" size="$md">
              {i18n.t('sales_portal.restaurant_selector.no_results_found')}
            </Typography>
          </YStack>
        }
        ListFooterComponent={
          isFetchingMore ? (
            <YStack alignItems="center" padding="$16">
              <Spinner />
            </YStack>
          ) : null
        }
        onEndReached={onEndReached}
        renderItem={renderItem}
      />
    );
  }

  return (
    <PushView
      aria-label={i18n.t(
        'sales_portal.order_entry.mobile_header.a11y.restaurants',
      )}
      onOpenChange={handleOpenChange}
      open={isOpen}
    >
      <PushView.Header backLabel={i18n.t('back')} />
      <PushView.Body padding="$0">
        <YStack paddingBottom="$8" paddingHorizontal="$16" paddingTop="$12">
          <SearchInput
            onSearchChange={handleSearchChange}
            placeholderText={i18n.t(
              'sales_portal.restaurant_selector.search_placeholder',
            )}
            searchText={searchText}
          />
        </YStack>
        {list}
      </PushView.Body>
    </PushView>
  );
};

const useStyles = createStyles((theme) => ({
  listContent: {
    paddingHorizontal: theme.sk.spacing.spaceXsmall,
  },
}));
